import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoices, Invoice } from '../../../hooks/useInvoices';
import { useTasks, Task } from '../../../hooks/useTasks';
import { useSupportTickets, SupportTicket } from '../../../hooks/useSupportTickets';

export interface Notification {
  id: string;
  type: 'invoice' | 'task' | 'ticket';
  title: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
  link: string;
  timestamp: Date;
}

export interface NotificationsWidgetProps {
  limit?: number;
}

const NotificationsWidget: React.FC<NotificationsWidgetProps> = ({ limit = 5 }) => {
  const navigate = useNavigate();
  const { invoices } = useInvoices();
  const { tasks } = useTasks();
  const { tickets } = useSupportTickets();

  const notifications = React.useMemo<Notification[]>(() => {
    const notifs: Notification[] = [];

    // Overdue invoices
    const now = new Date();
    (invoices || []).forEach((inv: Invoice) => {
      if (!inv.invoiceIncome || inv.invoiceIncome === 0) {
        const dueDate = inv.invoiceDue ? new Date(inv.invoiceDue) : null;
        if (dueDate && dueDate < now) {
          notifs.push({
            id: `invoice-${inv.invoiceId}`,
            type: 'invoice',
            title: 'Overdue Invoice',
            message: `Invoice ${inv.InvoiceNo} is overdue`,
            priority: 'high',
            link: `/client-invoices/${inv.invoiceId}`,
            timestamp: dueDate,
          });
        }
      }
    });

    // Overdue tasks
    const tasksArray = Array.isArray(tasks) ? tasks : [];
    tasksArray.forEach((task: Task) => {
      if (!task.taskStatus || task.taskStatus === 1) {
        const taskDate = task.taskDate ? new Date(task.taskDate) : null;
        if (taskDate && taskDate < now) {
          const priority = (task.taskPriority || 0) >= 3 ? 'high' : 'medium';
          notifs.push({
            id: `task-${task.taskId}`,
            type: 'task',
            title: 'Overdue Task',
            message: task.taskSubject || 'Untitled Task',
            priority,
            link: `/todos/${task.taskId}`,
            timestamp: taskDate,
          });
        }
      }
    });

    // High priority tickets
    const ticketsArray = Array.isArray(tickets) ? tickets : [];
    ticketsArray.forEach((ticket: SupportTicket) => {
      if (ticket.priority && ticket.priority >= 3) {
        notifs.push({
          id: `ticket-${ticket.id}`,
          type: 'ticket',
          title: 'High Priority Ticket',
          message: ticket.title,
          priority: 'high',
          link: `/support-ticket/${ticket.id}`,
          timestamp: new Date(ticket.createdAt),
        });
      }
    });

    // Sort by priority and timestamp
    return notifs
      .sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        if (priorityOrder[b.priority] !== priorityOrder[a.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return b.timestamp.getTime() - a.timestamp.getTime();
      })
      .slice(0, limit);
  }, [invoices, tasks, tickets, limit]);

  const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-orange-500';
      default:
        return 'bg-yellow-500';
    }
  };

  const getTypeIcon = (type: 'invoice' | 'task' | 'ticket') => {
    switch (type) {
      case 'invoice':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'task':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'ticket':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        {notifications.length > 0 && (
          <span className="px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded-full">
            {notifications.length}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {notifications.length > 0 ? (
          notifications.map((notif) => (
            <div
              key={notif.id}
              onClick={() => navigate(notif.link)}
              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border-l-4 border-gray-200 hover:border-teal-500"
              style={{ borderLeftColor: notif.priority === 'high' ? '#ef4444' : notif.priority === 'medium' ? '#f59e0b' : '#eab308' }}
            >
              <div className={`flex-shrink-0 p-2 rounded-lg ${getPriorityColor(notif.priority)} text-white`}>
                {getTypeIcon(notif.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                <p className="text-xs text-gray-600 mt-1 truncate">{notif.message}</p>
                <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notif.timestamp)}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p>All caught up! No notifications</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsWidget;

